import type { CreateHouseholdDto } from '@api/btlApplicationForm/types';

function toNonNegativeNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  return 0;
}

function pickNumber(source: Record<string, unknown> | undefined, key: string): number {
  return toNonNegativeNumber(source?.[key]);
}

type DependantFormValue = {
  id?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
};

// Every row is emitted, including a backfilled one the broker never touched. Omitting a row deletes
// it server-side and takes the household's derived dependant count down with it, so an incomplete
// legacy row is sent as its id alone and the server keeps what it already holds for the rest.
//
// An absent list is not the same as an empty one, so it returns undefined and the key is left off
// the payload entirely. The backend reads `dependants: []` as "this household has none" and derives
// both counts to zero — which is right when the broker cleared the list, and destructive when a
// household simply arrived without its dependants loaded.
function toDependants(value: unknown): DependantFormValue[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.map(dependant => {
    const row = dependant as Record<string, unknown>;
    const id = typeof row.id === 'string' && row.id ? row.id : undefined;
    const firstName = String(row.firstName ?? '').trim();
    const lastName = String(row.lastName ?? '').trim();
    const dateOfBirth = String(row.dateOfBirth ?? '');

    return {
      ...(id ? { id } : {}),
      ...(firstName ? { firstName } : {}),
      ...(lastName ? { lastName } : {}),
      ...(dateOfBirth ? { dateOfBirth } : {}),
    };
  });
}

type OccupierFormValue = {
  id?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  relationshipToPrimaryApplicant: string;
  salary: number;
};

function toOccupiers(value: unknown): OccupierFormValue[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(occupier => {
    const row = occupier as Record<string, unknown>;

    return {
      // Only send `id` for rows that already exist server-side; new rows get one minted there.
      ...(typeof row.id === 'string' && row.id ? { id: row.id } : {}),
      firstName: String(row.firstName ?? '').trim(),
      lastName: String(row.lastName ?? '').trim(),
      dateOfBirth: String(row.dateOfBirth ?? ''),
      relationshipToPrimaryApplicant: String(row.relationshipToPrimaryApplicant ?? ''),
      salary: toNonNegativeNumber(row.salary),
    };
  });
}

export function mapOoExpensesFormValuesToHousehold(values: Record<string, unknown>): CreateHouseholdDto {
  const income = (values.income as Record<string, unknown> | undefined) ?? {};
  const declaredExpenditure = (values.declaredExpenditure as Record<string, unknown> | undefined) ?? {};
  const creditCommitments = (values.creditCommitments as Record<string, unknown> | undefined) ?? {};

  const dependants = toDependants(values.dependants);

  return {
    ...(dependants ? { dependants } : {}),
    income: {
      childBenefit: pickNumber(income, 'childBenefit'),
      childTaxCredit: pickNumber(income, 'childTaxCredit'),
      workingTaxCredit: pickNumber(income, 'workingTaxCredit'),
      industrialInjuriesDisablementBenefit: pickNumber(income, 'industrialInjuriesDisablementBenefit'),
      personalIndependencePayment: pickNumber(income, 'personalIndependencePayment'),
    },
    declaredExpenditure: {
      travel: pickNumber(declaredExpenditure, 'travel'),
      childcare: pickNumber(declaredExpenditure, 'childcare'),
      csaMaintenance: pickNumber(declaredExpenditure, 'csaMaintenance'),
      councilTax: pickNumber(declaredExpenditure, 'councilTax'),
      serviceChargeGroundRent: pickNumber(declaredExpenditure, 'serviceChargeGroundRent'),
      other: pickNumber(declaredExpenditure, 'other'),
      basicEssentialLiving: pickNumber(declaredExpenditure, 'basicEssentialLiving'),
      basicQualityOfLiving: pickNumber(declaredExpenditure, 'basicQualityOfLiving'),
      dependantMaintenance: pickNumber(declaredExpenditure, 'dependantMaintenance'),
      educationFees: pickNumber(declaredExpenditure, 'educationFees'),
    },
    creditCommitments: {
      creditCardBalance: pickNumber(creditCommitments, 'creditCardBalance'),
      buyNowPayLater: pickNumber(creditCommitments, 'buyNowPayLater'),
      unsecuredLoans: pickNumber(creditCommitments, 'unsecuredLoans'),
      securedLoans: pickNumber(creditCommitments, 'securedLoans'),
      unsecuredLoansOnRevertDate: pickNumber(creditCommitments, 'unsecuredLoansOnRevertDate'),
    },
    occupiers: toOccupiers(values.occupiers),
  } as CreateHouseholdDto;
}
